import { Admin, CustomRoutes, localStorageStore, Resource, StoreContextProvider, bwLightTheme, bwDarkTheme } from 'react-admin';
import { BrowserRouter, Route } from 'react-router-dom';
import { httpClient, dataProvider } from './dataProvider';
import { LogtoAuthProvider } from './authProvider';
import { ClientCreate, ClientEdit, ClientList, ClientShow } from './components/clients';
import { BuildCreate, BuildList, BuildShow } from './components/builds';
import { ConfigurationEdit } from './components/configurations';
import { TemplateEdit, TemplateList } from './components/templates';
import { SecretUpload } from './components/secrets';
import { MetricsDashboard } from './components/metrics';
// T031: SharedSecrets components (Feature 010)
import { SharedSecretsList } from './components/sharedSecrets/SharedSecretsList';
import { SharedSecretsEdit } from './components/sharedSecrets/SharedSecretsEdit';
import { SharedSecretsCreate } from './components/sharedSecrets/SharedSecretsCreate';
import { AuditList } from './components/audit/AuditList';
import { Layout, Login } from './components/layout';
import LogtoClientClass, { type LogtoConfig } from '@logto/browser';
import { UserScope } from "@logto/react";

const store = localStorageStore(undefined, 'Zenky-deploy');


const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';
const appUrl = import.meta.env.VITE_APP_URL || 'http://localhost:3000';

const config: LogtoConfig = {
    endpoint: import.meta.env.VITE_LOGTO_ENDPOINT || 'https://7dhl03.logto.app/',
    appId: import.meta.env.VITE_LOGTO_APP_ID || 'y471j80y0ucn1q5c0d6he',
    resources: [apiUrl],
    scopes: [
        'clients:read',
        'clients:write',
        'configurations:read',
        'configurations:write',
        'secrets:read',
        'secrets:write',
        'builds:read',
        'builds:write',
        'builds:mass',
        'metrics:read',
        UserScope.Email,
        // Request a refresh token so getAccessToken can silently renew expired
        // access tokens. Raw string (ReservedScope.OfflineAccess) to avoid
        // coupling to a specific @logto enum location/version.
        'offline_access',
    ],
};

const logtoClient = new LogtoClientClass(config);

const logtoAuthProvider = LogtoAuthProvider(logtoClient, {
    logoutRedirectUri: `${appUrl}/login`,
    loginRedirectUri: `${appUrl}/auth-callback`,
    redirectOnCheckAuth: true
});

function App() {
    return (
        <BrowserRouter>
            <Admin
                dataProvider={dataProvider(httpClient(logtoClient))}
                store={store}
                authProvider={logtoAuthProvider}
                theme={bwLightTheme}
                darkTheme={bwDarkTheme}
                dashboard={MetricsDashboard}
                layout={Layout}
                loginPage={Login}
                disableTelemetry
                requireAuth
            >
                <Resource
                    name="clients"
                    list={ClientList}
                    show={ClientShow}
                    create={ClientCreate}
                    edit={ClientEdit}
                />
                <Resource
                    name="builds"
                    list={BuildList}
                    show={BuildShow}
                    create={BuildCreate}
                />
                <Resource
                    name="metrics"
                    list={MetricsDashboard}
                />
                {/* Feature 012: app templates */}
                <Resource
                    name="templates"
                    list={TemplateList}
                    edit={TemplateEdit}
                    recordRepresentation="name"
                    options={{ label: 'Templates' }}
                />
                {/* T031: Shared Secrets resource (Feature 010) */}
                <Resource
                    name="shared-secrets"
                    list={SharedSecretsList}
                    edit={SharedSecretsEdit}
                    create={SharedSecretsCreate}
                    options={{ label: 'Shared Secrets' }}
                />
                {/* Feature 013: audit of changes made through MCP */}
                <Resource
                    name="audit"
                    list={AuditList}
                    options={{ label: 'Audit' }}
                />
                <CustomRoutes>
                    <Route
                        path="/clients/:clientId/configuration"
                        element={<ConfigurationEdit/>}
                    />
                    <Route
                        path="/clients/:clientId/secrets"
                        element={<SecretUpload/>}
                    />
                </CustomRoutes>
            </Admin>
        </BrowserRouter>
    );
}

const AppWrapper = () => (
    <StoreContextProvider value={store}>
        <App/>
    </StoreContextProvider>
);

export default AppWrapper;
